// PRD 07 §2.2 #14 — Grade radio. Single-select G1/G2/G3.
//
// SHCT-841 — each option's label is the SAME `.grade grade-gN` badge the list row shows
// (prototype management-guidelines.html #mgModal), not bare colored text, and the radio
// accent is the badge's own --grade-gN token. See GradeBadge for the PRD 07 §4 / v1.7 basis.
//
// SHCT-636 — each option reads "단계 N" + its description (예방적 관리 / 자가처치 /
// 의료진 상담). It used to print the raw code identifier G1/G2/G3 as the bold label,
// which 공통 시스템정의 §3.3.3 reserves for data, never for display.

import { useTranslation } from 'react-i18next';

import { GUIDELINE_GRADES, type GuidelineGrade } from '../types/management-guidelines.types';

// The shared Grade tokens — the same ones `.grade-gN` paints the badge with.
const ACCENTS: Record<GuidelineGrade, string> = {
  G1: 'var(--grade-g1)',
  G2: 'var(--grade-g2)',
  G3: 'var(--grade-g3)',
};

interface GradeRadioGroupProps {
  value: GuidelineGrade | null;
  onChange: (next: GuidelineGrade) => void;
  // Renders a red ring around the selected row when validation fails so the
  // error message has a visual anchor. Slice 6 starts using this; Slice 5
  // ignores it because Save is disabled.
  invalid?: boolean;
  disabled?: boolean;
}

export function GradeRadioGroup({ value, onChange, invalid, disabled }: GradeRadioGroupProps) {
  const { t } = useTranslation('management-guidelines');

  return (
    <div
      style={{ display: 'flex', gap: 12, marginTop: 4, flexWrap: 'wrap' }}
      role="radiogroup"
      aria-invalid={invalid || undefined}
    >
      {GUIDELINE_GRADES.map((grade) => {
        const selected = value === grade;
        return (
          <label
            key={grade}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontSize: 14,
              cursor: disabled ? 'not-allowed' : 'pointer',
              padding: '6px 14px',
              border: `1.5px solid ${selected ? ACCENTS[grade] : 'var(--border)'}`,
              borderRadius: 'var(--radius-sm)',
              background: selected ? '#fff' : 'transparent',
              opacity: disabled ? 0.6 : 1,
            }}
          >
            <input
              type="radio"
              name="guideline-grade"
              value={grade}
              checked={selected}
              onChange={() => onChange(grade)}
              disabled={disabled}
              style={{ accentColor: ACCENTS[grade] }}
            />
            <span className={`grade grade-${grade.toLowerCase()}`} style={{ whiteSpace: 'nowrap' }}>
              {t(`gradeLabels.${grade}`)}
            </span>
            <span>{t(`gradeDescriptions.${grade}`)}</span>
          </label>
        );
      })}
    </div>
  );
}
